import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, inject } from '@angular/core';

import { OfferSource } from '@cds';
import { EventParticipant, EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { FavouriteType } from '@frontend/sports/favourites/core/feature';
import { FavouriteToggleComponent, FavouriteToggleTracking } from '@frontend/sports/favourites/feature/toggle';
import { TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';

@Component({
    selector: 'ms-favourite-participant-toggle',
    template: `
        <ms-favourite-toggle
            [id]="participant.id?.toString() ?? ''"
            [name]="name"
            [sport]="sport"
            [type]="type"
            [favIconSize]="favToggleIconSize"
            [static]="static"
            [participantImage]="participantImage"
            [tracking]="favouriteTracking"
            (toggle)="toggle.emit($event)" />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FavouriteToggleComponent],
})
export class FavouriteParticipantToggleComponent implements OnChanges {
    @Input() participant: EventParticipant;
    @Input() sport: number;
    @Input() source?: OfferSource;
    @Input() static: boolean;
    @Input() tracking: TrackingSource;
    @Input() participantImage?: EventParticipantImage;
    _favToggleIconSize: string;

    @Input() set favToggleIconSize(value: string) {
        this._favToggleIconSize = value;
    }

    @Output() toggle = new EventEmitter<boolean>();
    epcotEnabled: boolean = inject(EpcotConfig).isEnabled;
    get favToggleIconSize(): string {
        return this._favToggleIconSize ?? (this.epcotEnabled ? '16' : '14');
    }

    name: string;
    type: FavouriteType;

    get favouriteTracking(): FavouriteToggleTracking {
        return {
            name: this.getName(),
            additional: {
                [trackingConstants.SPORT_ID]: this.sport.toString(),
            },
            ...this.tracking,
        };
    }

    ngOnChanges(changes: ISimpleChanges<FavouriteParticipantToggleComponent>): void {
        if (changes.participant) {
            const previous = changes.participant.previousValue ? changes.participant.previousValue.id : undefined;
            const current = changes.participant.currentValue ? changes.participant.currentValue.id : undefined;

            if (previous !== current) {
                this.name = this.getName();
            }
        }

        if (changes.source) {
            this.type = this.getType();
        }
    }

    private getType(): FavouriteType {
        if (!this.source || this.source === OfferSource.V1) {
            return FavouriteType.Participant;
        }

        return FavouriteType.ParticipantV2;
    }

    private getName(): string {
        return this.participant.country ? `${this.participant.name} (${this.participant.country})` : this.participant.name;
    }
}
